import { Link } from "react-router-dom";
import { Fingerprint, CalendarDays, CalendarClock, Users, Wallet, BarChart3, ShieldCheck, Smartphone, Cloud, CheckCircle2, Target, Eye } from "lucide-react";

const offers = [
  { icon: Fingerprint, t: "Employee Attendance Management" },
  { icon: CalendarDays, t: "Leave & Holiday Management" },
  { icon: CalendarClock, t: "Shift Scheduling" },
  { icon: Users, t: "Employee Directory" },
  { icon: Wallet, t: "Payroll Support" },
  { icon: BarChart3, t: "Reports & Analytics" },
  { icon: ShieldCheck, t: "Role-Based Access Controls" },
  { icon: Smartphone, t: "Mobile Accessibility" },
  { icon: Cloud, t: "Secure Cloud-Based Platform" },
];

const whys = [
  "Simple and intuitive user experience",
  "Designed for businesses of all sizes",
  "Secure cloud infrastructure",
  "Scalable as your organization grows",
  "Reliable customer support",
  "Continuous product improvements and feature updates",
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="py-20 md:py-28 hero-grid-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-4">About Us</div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]">
            Smarter Workforce Management, Built for Modern Businesses
          </h1>
          <div className="mt-6 space-y-4 text-lg text-slate-600 leading-relaxed text-left md:text-center">
            <p>At <b>Clocked In One</b>, we believe managing your workforce should be simple, efficient, and accessible from anywhere.</p>
            <p>Clocked In One is a modern workforce management platform designed to help businesses streamline attendance, leave management, shift scheduling, employee records, payroll support, and day-to-day HR operations—all through a single, intuitive platform.</p>
            <p>Whether you're a growing startup, a mid-sized business, or a large enterprise, our goal is to eliminate manual processes, reduce administrative overhead, and give your team more time to focus on what matters most: growing your business.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-3">What We Offer</div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1F1E4E]">The essential tools to manage your workforce efficiently.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((o, i) => {
              const I = o.icon;
              return (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-[#F8FAFC] p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
                  <div className="h-10 w-10 rounded-lg bg-[#E6F6EC] flex items-center justify-center shrink-0">
                    <I className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div className="font-bold text-[#1F1E4E] text-sm">{o.t}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-3">Why Choose Clocked In One?</div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1F1E4E]">Built to help you move faster, with confidence.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {whys.map((w, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5">
                <CheckCircle2 className="h-5 w-5 text-[#00A651] shrink-0 mt-0.5" />
                <span className="text-slate-700">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 p-8">
            <Target className="h-8 w-8 text-[#00A651]" />
            <div className="mt-4 text-2xl font-extrabold text-[#1F1E4E]">Our Mission</div>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Our mission is to simplify workforce management by providing businesses with reliable, secure, and easy-to-use technology that improves productivity, transparency, and operational efficiency.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-8">
            <Eye className="h-8 w-8 text-[#00A651]" />
            <div className="mt-4 text-2xl font-extrabold text-[#1F1E4E]">Our Vision</div>
            <p className="mt-3 text-slate-600 leading-relaxed">
              To become a trusted workforce management solution that empowers organizations worldwide to build more connected, efficient, and productive workplaces.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1F1E4E] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-3">Get in Touch</div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Interested in learning more or scheduling a demo?
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed">
            We'd love to hear from you. Visit our Contact Us page or submit an enquiry through our website, and a member of our team will get in touch.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" data-testid="about-contact-btn" className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3">
              Contact Us
            </Link>
            <Link to="/contact?intent=book_demo" data-testid="about-demo-btn" className="border-2 border-white/30 hover:border-white text-white font-bold rounded-lg px-6 py-3">
              Request a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
